import React from "react";
import { Link } from "react-router-dom";
import { AlertCircle, PoundSterling, Shield } from "lucide-react";
import SEO from "../components/SEO";

export const HMRCPenaltiesGuide = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "HMRC Penalties Explained: How to Avoid Them",
    "description": "Complete guide to HMRC penalties for UK businesses. Late filing, late payment, incorrect returns. Penalty amounts, appeals, and how to avoid them.",
    "author": { "@type": "Organization", "name": "Brightson Accounting" },
    "publisher": { "@type": "Organization", "name": "Brightson Accounting" },
    "datePublished": "2026-04-06"
  };

  return (
    <>
      <SEO
        title="HMRC Penalties Guide UK 2026 | Late Filing & Payment | Brightson"
        description="HMRC penalties explained: late filing £100+, late payment interest, incorrect returns. Penalty amounts, how to appeal, and avoidance strategies for UK businesses."
        canonical="/blog/hmrc-penalties-guide"
        schema={articleSchema}
      />

      <article className="blog-article">
        <header className="article-header">
          <div className="container">
            <div className="article-meta">
              <span className="article-category"><AlertCircle size={16} /> Staying Compliant</span>
              <span className="article-date">6 April 2026</span>
              <span className="article-read-time">6 min read</span>
            </div>

            <h1>HMRC Penalties Explained: How to Avoid Them</h1>
            <p className="article-subtitle">
              Complete guide to HMRC penalties — late filing, late payment, incorrect returns, and how to avoid them all
            </p>

            <div className="article-author">
              <div className="author-info">
                <strong>Brightson Accounting (FCCA Qualified)</strong>
                <span>Tax Compliance Specialists | Wolverhampton</span>
              </div>
            </div>
          </div>
        </header>

        <section className="article-content">
          <div className="container">
            <div className="article-body">
              
              <p className="article-lead">
                HMRC penalties are avoidable. At Brightson Accounting, we help businesses across Wolverhampton and the West Midlands avoid the biggest causes: poor organisation and last-minute filing.
              </p>

              <p>Understanding penalty rules helps you avoid costly mistakes.</p>

              <div className="info-box" style={{ background: '#fee', borderLeft: '4px solid #dc3545' }}>
                <strong>⚠️ Quick Summary</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li>Late filing → £100+ (increases with delay)</li>
                  <li>Late payment → Interest at 7.75%/year + penalties</li>
                  <li>Repeated lateness → Higher penalties</li>
                  <li>Incorrect returns → Penalties up to 100% of tax owed</li>
                  <li>Most penalties are preventable with good systems</li>
                </ul>
              </div>

              <h2>Self Assessment Penalties</h2>

              <h3>Late Filing Penalties</h3>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>How Late</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Penalty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>1 day late</td>
                    <td style={{ padding: '0.75rem' }}>£100 fixed penalty</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>3 months late</td>
                    <td style={{ padding: '0.75rem' }}>£100 + £10 per day (max £900)</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>6 months late</td>
                    <td style={{ padding: '0.75rem' }}>£100 + £900 + £300 or 5% tax (whichever higher)</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>12 months late</td>
                    <td style={{ padding: '0.75rem' }}>All above + another £300 or 5% tax</td>
                  </tr>
                </tbody>
              </table>

              <p><strong>Example:</strong> File 4 months late with £5,000 tax due:</p>
              <ul>
                <li>£100 initial penalty</li>
                <li>£900 daily penalties (£10 x 90 days)</li>
                <li><strong>Total:</strong> £1,000 in penalties alone</li>
              </ul>

              <h3>Late Payment Penalties</h3>

              <p>Tax due by <strong>31 January</strong>. If you pay late:</p>

              <ul>
                <li><strong>30 days late:</strong> 5% of unpaid tax</li>
                <li><strong>6 months late:</strong> Additional 5% of unpaid tax</li>
                <li><strong>12 months late:</strong> Another 5% of unpaid tax</li>
                <li><strong>Plus:</strong> Interest charged from 31 January (currently 7.75%/year)</li>
              </ul>

              <p>Full guide: <Link to="/blog/self-assessment-compliance">Self Assessment Compliance: Avoid Costly Errors</Link></p>

              {/* Mid-Article CTA */}
              <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
                <h3>💡 Want to see how much tax you could save?</h3>
                <p>Most UK business owners are overpaying without realising it.</p>
                <Link to="/corporation-tax-calculator" className="article-cta-button">
                  👉 Try the Corporation Tax Calculator
                </Link>
              </div>

              <h2>Corporation Tax Penalties</h2>

              <h3>Late Filing (CT600)</h3>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>How Late</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Penalty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>1 day late</td>
                    <td style={{ padding: '0.75rem' }}>£100</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>3 months late</td>
                    <td style={{ padding: '0.75rem' }}>£200</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>6+ months late</td>
                    <td style={{ padding: '0.75rem' }}>£200 + 10% of tax or £500 (whichever higher)</td>
                  </tr>
                </tbody>
              </table>

              <h3>Late Payment</h3>

              <p>Corporation Tax due <strong>9 months + 1 day</strong> after year-end.</p>

              <p>Late payment = interest charged at 7.75%/year (no fixed penalties, just interest).</p>

              <p>Full guide: <Link to="/blog/corporation-tax-compliance">Corporation Tax Compliance Explained</Link></p>

              <h2>VAT Penalties</h2>

              <h3>Late Filing</h3>

              <p>VAT returns due <strong>1 month + 7 days</strong> after quarter end.</p>

              <p>New Points-Based System (from 2023):</p>
              <ul>
                <li>Each late return = 1 penalty point</li>
                <li>4 points (annual filers) or higher (quarterly filers) = £200 penalty</li>
                <li>Each further late return = another £200</li>
                <li>Points expire after compliance period</li>
              </ul>

              <h3>Late Payment</h3>

              <p>VAT payment also due 1 month + 7 days after quarter.</p>

              <ul>
                <li><strong>15 days late:</strong> 2% of unpaid VAT</li>
                <li><strong>30 days late:</strong> Another 2%</li>
                <li><strong>Plus:</strong> Interest on unpaid VAT</li>
              </ul>

              <p>Full guide: <Link to="/blog/vat-compliance-mistakes">VAT Compliance: Common Mistakes</Link></p>

              <h2>Incorrect Return Penalties</h2>

              <p>If you submit an incorrect return (overestimating expenses, underreporting income), HMRC can charge penalties based on the "behaviour":</p>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Behaviour</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Penalty Range</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Reasonable care taken</td>
                    <td style={{ padding: '0.75rem' }}>No penalty</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Careless (genuine mistake)</td>
                    <td style={{ padding: '0.75rem' }}>0-30% of tax owed</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>Deliberate (but not concealed)</td>
                    <td style={{ padding: '0.75rem' }}>20-70% of tax owed</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>Deliberate and concealed</td>
                    <td style={{ padding: '0.75rem' }}>30-100% of tax owed</td>
                  </tr>
                </tbody>
              </table>

              <p><strong>How to Avoid:</strong></p>
              <ul>
                <li>Keep accurate records</li>
                <li>Only claim allowable expenses</li>
                <li>Declare all income</li>
                <li>Work with a qualified accountant</li>
                <li>If you discover an error, tell HMRC immediately (reduces penalties)</li>
              </ul>

              <h2>How to Appeal a Penalty</h2>

              <p>You can appeal if you have a "reasonable excuse" for missing a deadline:</p>

              <h3>Valid Reasons:</h3>
              <ul>
                <li>Serious illness or bereavement</li>
                <li>Unexpected hospital stay</li>
                <li>Computer or software failure (if reported to HMRC immediately)</li>
                <li>Fire, flood, or theft of records</li>
                <li>Postal delays (only if you can prove you posted in time)</li>
              </ul>

              <h3>NOT Valid:</h3>
              <ul>
                <li>"I forgot"</li>
                <li>"I was busy"</li>
                <li>"I didn't know the deadline"</li>
                <li>Relying on someone else who let you down</li>
              </ul>

              <p><strong>How to Appeal:</strong></p>
              <ol>
                <li>Write to HMRC within 30 days of receiving the penalty notice</li>
                <li>Explain your reasonable excuse with evidence</li>
                <li>Pay the tax owed (penalty can be disputed separately)</li>
                <li>Wait for HMRC's decision (usually 45 days)</li>
              </ol>

              <p>If HMRC rejects your appeal, you can escalate to the Tax Tribunal.</p>

              <h2>How to Avoid All HMRC Penalties</h2>

              <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                <strong>✅ Penalty Prevention Checklist</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li>Set calendar reminders 2 weeks before every deadline</li>
                  <li>Use accounting software with automated deadline alerts</li>
                  <li>File returns early (don't wait until deadline day)</li>
                  <li>Keep accurate digital records year-round</li>
                  <li>Work with an accountant who tracks deadlines for you</li>
                  <li>If you can't file on time, contact HMRC immediately</li>
                </ul>
              </div>

              <p>Full guide: <Link to="/blog/hmrc-deadlines-uk">HMRC Deadlines You Cannot Afford to Miss</Link></p>

              <h2>How Brightson Accounting Can Help</h2>

              <p>We help businesses across Wolverhampton and the West Midlands avoid penalties by:</p>
              <ul>
                <li>Tracking all filing and payment deadlines</li>
                <li>Filing returns on time (we don't miss deadlines)</li>
                <li>Maintaining accurate records (penalty-proof)</li>
                <li>Handling HMRC penalty appeals</li>
                <li>Setting up systems so compliance becomes automatic</li>
              </ul>

              <p>
                Our clients rarely receive penalties — because we handle compliance for them. Want to stay compliant and <Link to="/corporation-tax-calculator">reduce your corporation tax</Link>? <Link to="/contact">Speak to an accountant</Link> today.
              </p>

            </div>

            {/* End CTA */}
            <div className="article-cta-box" style={{ marginTop: '3rem' }}>
              <h2>🚀 Ready to stay compliant and reduce your tax bill?</h2>
              <p>We help businesses across Wolverhampton and the West Midlands:</p>
              <ul>
                <li>Stay compliant with HMRC</li>
                <li>Reduce tax legally</li>
                <li>Avoid penalties and investigations</li>
              </ul>
              <Link to="/contact" className="article-cta-button">👉 Book a Free Consultation</Link>
            </div>

            <div className="related-articles" style={{ marginTop: '3rem' }}>
              <h3>Related Guides</h3>
              <div className="related-links">
                <Link to="/blog/hmrc-compliance-guide-uk" className="related-link">HMRC Compliance Guide</Link>
                <Link to="/blog/hmrc-deadlines-uk" className="related-link">HMRC Deadlines</Link>
                <Link to="/blog/self-assessment-compliance" className="related-link">Self Assessment Compliance</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default HMRCPenaltiesGuide;